import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { AccountMembership, APIService, CreateCustomPropertyDefinitionsInput, CustomPropertyDefinitions, UpdateCustomPropertyDefinitionsInput } from '../../../../API.service.custom';
import { ToastrService } from 'ngx-toastr';
import { MatButton } from '@angular/material/button';
import { AiVisitOutcome } from '../ai-workspace-setup.models';
import { fetchAllPropertiesByAccountAndType } from 'app/shared/pagination/paginated-api-queries';
import { stringToUppercaseUnderscore } from '../../../../../globals/functions';

@Component({
  selector: 'ws-visit-outcomes',
  templateUrl: './ws-visit-outcomes.component.html',
  imports: [FormsModule, NgClass, MatButton, DragDropModule]
})
export class WsVisitOutcomesComponent implements OnInit, OnChanges {
  @Input() suggestions: AiVisitOutcome[];
  @Input() activeAccount: AccountMembership;
  @Input() testMode = false;
  @Output() completed = new EventEmitter<void>();
  @Output() feedbackRequested = new EventEmitter<string>();

  mode: 'choose' | 'outcomes' = 'choose';
  outcomes: AiVisitOutcome[] = [];
  private existingOutcomes: AiVisitOutcome[] = [];
  isSaving = false;
  existingProperty: CustomPropertyDefinitions = null;
  feedback = '';
  newOutcomeName = '';
  newOutcomeNature: 'positive' | 'negative' | 'neutral' = 'neutral';

  constructor(
    private api: APIService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    if (!this.testMode) this.checkExistingData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['suggestions'] && this.suggestions) {
      this.initOutcomes();
    }
  }

  private initOutcomes() {
    this.outcomes = (this.suggestions || []).map(o => ({
      ...o,
      value: o.value || stringToUppercaseUnderscore(o.name),
      selected: o.selected !== false,
    }));
  }

  private async checkExistingData() {
    if (!this.activeAccount?.accountId) return;
    try {
      const properties = await fetchAllPropertiesByAccountAndType(
        this.api,
        this.activeAccount.accountId
      );
      if (properties) {
        const existing = properties.find((p: any) => p.propertyName === 'visitOutcome');
        if (existing) {
          this.existingProperty = existing as CustomPropertyDefinitions;
          // Pre-populate outcomes from existing data (but keep gate visible)
          if (existing.possibleValues?.length) {
            this.existingOutcomes = existing.possibleValues.map((pv: any) => {
              const attrs = pv.otherAttributes ? JSON.parse(pv.otherAttributes) : {};
              return {
                name: attrs.outcomeTitle || pv.viewValue || pv.value,
                value: pv.value,
                nature: attrs.nature || 'neutral',
                startsNewDeal: attrs.createNewDealManual || attrs.createNewDeal || false,
                selected: true,
              } as AiVisitOutcome;
            });
          }
        }
      }
    } catch (e) {
      console.error('Error checking existing visit outcomes:', e);
    }
  }

  // --- Gate ---
  selectDoVisits() {
    this.mode = 'outcomes';
    // Use existing data if available, otherwise use AI suggestions
    if (this.existingOutcomes.length > 0) {
      this.outcomes = this.existingOutcomes;
    } else {
      this.initOutcomes();
    }
  }

  skipVisits() {
    this.completed.emit();
  }

  // --- Outcome helpers ---
  get positiveOutcomes() { return this.outcomes.filter(o => o.nature === 'positive'); }
  get negativeOutcomes() { return this.outcomes.filter(o => o.nature === 'negative'); }
  get neutralOutcomes() { return this.outcomes.filter(o => o.nature === 'neutral'); }

  toggleOutcome(outcome: AiVisitOutcome) {
    outcome.selected = !outcome.selected;
  }

  removeOutcome(outcome: AiVisitOutcome) {
    this.outcomes = this.outcomes.filter(o => o !== outcome);
  }

  addOutcome() {
    if (!this.newOutcomeName.trim()) return;
    this.outcomes.push({
      name: this.newOutcomeName.trim(),
      value: stringToUppercaseUnderscore(this.newOutcomeName.trim()),
      nature: this.newOutcomeNature,
      startsNewDeal: false,
      selected: true,
    });
    this.newOutcomeName = '';
  }

  reorderOutcomes(event: CdkDragDrop<AiVisitOutcome[]>, nature: string) {
    const subset = this.outcomes.filter(o => o.nature === nature);
    moveItemInArray(subset, event.previousIndex, event.currentIndex);
    const positive = nature === 'positive' ? subset : this.positiveOutcomes;
    const neutral = nature === 'neutral' ? subset : this.neutralOutcomes;
    const negative = nature === 'negative' ? subset : this.negativeOutcomes;
    this.outcomes = [...positive, ...neutral, ...negative];
  }

  async save() {
    if (this.isSaving) return;
    const selected = this.outcomes.filter(o => o.selected);
    if (selected.length === 0) {
      this.toastr.warning('Please select at least one visit outcome');
      return;
    }

    if (this.testMode) {
      this.toastr.success('Test mode: Visit outcomes saved');
      this.completed.emit();
      return;
    }

    this.isSaving = true;

    try {
      const possibleValues = selected.map(o => ({
        value: o.value || stringToUppercaseUnderscore(o.name),
        viewValue: o.name.trim(),
        otherAttributes: JSON.stringify({
          outcomeTitle: o.name.trim(),
          nature: o.nature,
          createNewDeal: false,
          createNewDealManual: o.startsNewDeal || false,
        }),
      }));

      if (this.existingProperty) {
        const input = <UpdateCustomPropertyDefinitionsInput>{
          id: this.existingProperty.id,
          accountId: this.existingProperty.accountId,
          possibleValues,
          updaedById: this.activeAccount.userId,
        };
        await this.api.UpdateCustomPropertyDefinitions(input);
      } else {
        const input = <CreateCustomPropertyDefinitionsInput>{
          accountId: this.activeAccount.accountId,
          propertyName: 'visitOutcome',
          displayName: 'Visit Outcome',
          description: 'Visit Outcome',
          scalarType: 'Option',
          isSystemProperty: true,
          parentObjectType: 'Engagement',
          possibleValues,
          createdById: this.activeAccount.userId,
          updaedById: this.activeAccount.userId,
        };
        await this.api.CreateCustomPropertyDefinitions(input);
      }

      this.toastr.success('Visit outcomes saved');
      this.completed.emit();
    } catch (e: any) {
      console.error('Error saving visit outcomes:', e);
      this.toastr.error(e?.errors?.[0]?.message || 'Failed to save');
    } finally {
      this.isSaving = false;
    }
  }

  submitFeedback() {
    if (this.feedback.trim()) {
      this.feedbackRequested.emit(this.feedback.trim());
      this.feedback = '';
    }
  }
}
